import {
  formatCardTimeRange,
  formatPlainTime,
  getSessionSubstitutionRequestSummary,
  InstantInterval,
  instantToDate,
  isWithinN,
  type SessionSubstitutionSummary,
} from '@airreading/common';
import {
  CoachPortalSession,
  CoachPortalSubstitutionRequest,
  CoachSessionRemarkV2,
  SessionStatus,
} from '@airreading/common/data';
import { createContext, type ReactNode } from 'react';
import { Temporal } from 'temporal-polyfill';

import type { CalendarView, ConcreteEvent, EventsWithContext } from './session-schedule-calendar-types.js';

//#region Event popover / toolbar context (RBC + Schedule-X)

export interface EventContextValue {
  isLoading: boolean;
  activeEventId: string | null;
  clearActiveEvent: () => void;
  sessionById: Map<string, CoachPortalSession>;
  substitutionRequestSummaryBySessionId: Map<string, SessionSubstitutionSummary<CoachPortalSubstitutionRequest>>;
  view: CalendarView;
  toolbarLeftAddon: ReactNode | null;
}

export const EventContext = createContext<EventContextValue>({
  isLoading: false,
  activeEventId: null,
  clearActiveEvent: () => {},
  sessionById: new Map<string, CoachPortalSession>(),
  substitutionRequestSummaryBySessionId: new Map<string, SessionSubstitutionSummary<CoachPortalSubstitutionRequest>>(),
  view: 'week',
  toolbarLeftAddon: null,
});

//#endregion

//#region Session → calendar events (shared by RBC and Schedule-X)

/**
 * Maps coach sessions to calendar events for {@link SessionScheduleCalendar}.
 * Exported for route-level `useMemo` (e.g. coach classes calendar); not memoized here.
 * Active date / view remain parent-controlled via URL search state.
 */
export function eventsFromSessions(sessions: CoachPortalSession[], timeZone: string): EventsWithContext {
  const events: ConcreteEvent[] = [];
  const sessionById = new Map<string, CoachPortalSession>();

  if (!sessions.length) {
    return { events, sessionById };
  }

  for (const session of sessions) {
    if (!shouldShowSessionOnCoachCalendar(session)) {
      continue;
    }

    const { id, startInstant, endInstant, status, coachSessionRemarksV2 } = session;
    if (Temporal.Instant.compare(startInstant, endInstant) > 0) {
      continue;
    }

    const start = instantToDate(startInstant);
    const end = instantToDate(endInstant);

    sessionById.set(id, session);

    const startZoned = startInstant.toZonedDateTimeISO(timeZone);
    const endZoned = endInstant.toZonedDateTimeISO(timeZone);
    const timeRangeFormatted = formatCardTimeRange(
      formatPlainTime(startZoned.toPlainTime()),
      formatPlainTime(endZoned.toPlainTime()),
    );

    events.push({
      id,
      title: studentNamesOfSession(session).join(', '),
      start,
      end,
      timeRangeFormatted,
      durationMinutes: startInstant.until(endInstant).round({ largestUnit: 'minute' }).minutes,
      status,
      startDate: startZoned.toPlainDate(),
      eventInterval: InstantInterval.from(startInstant, endInstant),
      allDay: false,
      coachSessionRemarksV2,
    });
  }

  return { events, sessionById };
}

/** Cancelled >12h before start are hidden; aligns with coach calendar API remark filters. */
function shouldShowSessionOnCoachCalendar(session: CoachPortalSession): boolean {
  const { status, hoursCanceledBeforeStart } = session;
  if (status !== SessionStatus.cancelled) {
    return true;
  }
  return hoursCanceledBeforeStart !== null && isWithinN(hoursCanceledBeforeStart, 12);
}

function studentNamesOfSession(session: CoachPortalSession): string[] {
  return session.students.map((s) => s.coachFacingDisplayName ?? '').toSorted();
}

//#endregion

//#region Substitution summaries + event styling

export function substitutionRequestSummariesForSessions(
  sessionsById: EventContextValue['sessionById'],
): Map<string, SessionSubstitutionSummary<CoachPortalSubstitutionRequest>> {
  const result = new Map<string, SessionSubstitutionSummary<CoachPortalSubstitutionRequest>>();
  for (const [id, session] of sessionsById.entries()) {
    result.set(id, getSessionSubstitutionRequestSummary(session));
  }
  return result;
}

/** Tailwind class map for event block background (legacy RBC + Schedule-X custom components). */
export function backgroundColorForCoachSessionRemarks(coachSessionRemarksV2: CoachSessionRemarkV2[]) {
  const isCancelledWithin12Hours = coachSessionRemarksV2.includes(CoachSessionRemarkV2.canceledWithin12Hours);
  const isSubbedWithin12Hours = coachSessionRemarksV2.includes(CoachSessionRemarkV2.subClassWithin12Hours);
  const isSubbedOutside12Hours = coachSessionRemarksV2.includes(CoachSessionRemarkV2.subClass);

  return {
    'bg-event-scheduled': !isCancelledWithin12Hours && !isSubbedWithin12Hours && !isSubbedOutside12Hours,
    'bg-event-cancelled': isCancelledWithin12Hours,
    'bg-event-subbed-outside-12-hours': !isCancelledWithin12Hours && isSubbedOutside12Hours,
    'bg-event-subbed-within-12-hours': !isCancelledWithin12Hours && isSubbedWithin12Hours,
  };
}

/**
 * White separators between back-to-back session blocks in week/day time grids.
 * Each block gets 2px top + 2px bottom → 4px white gap between adjacent sessions.
 */
export const coachCalendarTimeGridSessionGapClasses =
  'box-border border-x-0 border-t-2 border-b-2 border-solid border-white';

/**
 * Surface classes for coach calendar event blocks.
 * Matches legacy RBC `.rbc-event` (`color: #fff`, `border-radius: 5px`) plus remark backgrounds.
 */
export function coachCalendarEventSurfaceClasses(
  coachSessionRemarksV2: CoachSessionRemarkV2[],
  isPast: boolean,
): Record<string, boolean> {
  return {
    ...(!isPast ? backgroundColorForCoachSessionRemarks(coachSessionRemarksV2) : {}),
    'bg-air-light-grey': isPast,
    'text-white': true,
    'rounded-[5px]': true,
  };
}

//#endregion
